import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { Dropdown } from "primereact/dropdown";

import { Can } from "configs/CASLContext";

import { deleteTemplate, deleteTemplates, findTemplates } from "services/TemplateService";
import { TemplateContext } from "components/template/TemplateContext";
import { Confirmation } from "components/Confirmation";
import { TemplateEditor } from "components/template/TemplateEditor";
import { create, del, edit, Template, X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";
import BlockDetail from "components/block-detail/BlockDetail";
import { actionUpdate, actionView } from "utils/action";

export const TemplateList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["template", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const templateContext = useContext(TemplateContext);
    const { template, resetTemplate, setTemplate, templates, setTemplates, submitted, setSubmitted, templateDialog, setTemplateDialog } = templateContext;

    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [deleteTemplateDialog, setDeleteTemplateDialog] = useState(false);
    const [deleteTemplatesDialog, setDeleteTemplatesDialog] = useState(false);

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [checkLoad, setCheckLoad] = useState(true);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            code: { value: "", matchMode: "ilike" },
            name: { value: "", matchMode: "ilike" },
            type: { value: null, matchMode: "equals" },
            enabled: { value: null, matchMode: "equals" },
        },
    });
    const [globalFilter, setGlobalFilter] = useState(null);

    const templateTypes = [
        { label: t("sms"), value: "SMS" },
        { label: t("notification"), value: "NOTIFICATION" },
        { label: t("email"), value: "EMAIL" },
    ];

    useEffect(() => {
        if (checkLoad) {
            loadLazyData();
            setCheckLoad(false);
        }
    }, [lazyParams]);

    useEffect(() => {
        if (submitted) {
            loadLazyData();
            setSubmitted(false);
        }
    }, [submitted]);

    const loadLazyData = () => {
        setLoading(true);
        setSelectedTemplate(null);
        setTemplate(null);
        findTemplates(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data;
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                if (dataRes.length > 0) {
                    setSelectedTemplate(dataRes[0]);
                }
                setTemplates(dataRes);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const openNewDialog = () => {
        resetTemplate();
        setSubmitted(false);
        setTemplateDialog(true);
    };

    const openEditDialog = () => {
        setTemplate({ ...selectedTemplate, action: actionUpdate });
        setSubmitted(false);
        setTemplateDialog(true);
    };

    const openViewDialog = (template) => {
        setTemplate({ ...template, action: actionView });
        setSubmitted(false);
        setTemplateDialog(true);
    };

    const hideDeleteTemplatesDialog = () => {
        setDeleteTemplatesDialog(false);
    };

    const confirmDeleteTemplate = (template) => {
        setTemplate(template);
        setDeleteTemplateDialog(true);
    };

    const hideDeleteTemplateDialog = () => {
        setDeleteTemplateDialog(false);
    };

    const confirmDeleteSelected = () => {
        setDeleteTemplatesDialog(true);
    };

    const deleteTemplateAction = () => {
        deleteTemplate(headers, template.id)
            .then(() => {
                setDeleteTemplateDialog(false);
                setCheckLoad(true);
                setLazyParams({
                    ...lazyParams,
                    page: 0,
                    first: 0,
                });
                resetTemplate();
                setSelectedTemplate(null);
                toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.deleted", { ns: "common", object: t("template") }), life: 3000 });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const deleteSelectedTemplatesAction = () => {
        let ids = [selectedTemplate.id];
        deleteTemplates(headers, ids)
            .then(() => {
                setDeleteTemplatesDialog(false);
                setCheckLoad(true);
                setLazyParams({
                    ...lazyParams,
                    page: 0,
                    first: 0,
                });
                setSelectedTemplate(null);
                toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.deleted", { ns: "common", object: t("templates") }), life: 3000 });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const checkboxFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <TriStateCheckbox value={options.value} onChange={(e) => options.filterApplyCallback(e.value)} />
            </div>
        );
    };

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target?.value || "")} />
            </div>
        );
    };

    const templateTypeFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={templateTypes} onChange={(e) => options.filterApplyCallback(e.value)} />;
    };

    const actionTemplate = () => {
        return (
            <div className="grid">
                <div className="col-12 tex-center mt-2" style={{ display: "flex", justifyContent: "center" }}>
                    {/* tạo mới */}
                    <Button label={t("new", { ns: "common" })} icon="pi pi-plus" className="p-button mr-2" onClick={openNewDialog} />
                    {/* sửa */}
                    <Button disabled={!selectedTemplate} label={t("edit", { ns: "common" })} icon="pi pi-pencil" className="p-button mr-2" onClick={openEditDialog} />
                    {/* xóa */}
                    <Button disabled={!selectedTemplate} label={t("delete", { ns: "common" })} icon="pi pi-trash" className="p-button-danger p-button mr-2" onClick={confirmDeleteSelected} />
                </div>
            </div>
        );
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("template.list")}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder={t("search", { ns: "common" })} />
            </span>
        </div>
    );

    const templateTypeBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex">
                    <p>{t(String(rowData.type).toLowerCase())}</p>
                </div>
            </>
        );
    };

    const checkboxBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <Checkbox checked={rowData.enabled} readOnly />
                </div>
            </>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Can I={edit} a={Template}>
                    <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openViewDialog(rowData)} />
                </Can>
                {/* <Can I={del} a={Template}>
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" tooltip={t("delete", { ns: "common" })} onClick={() => confirmDeleteTemplate(rowData)} />
                </Can> */}
            </div>
        );
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <div className="grid">
            {/* Mã */}
            <div className="col-12 md:col-4">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "66px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="code"
                    >
                        {t("template.code")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="code"
                            name="code"
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        code: {
                                            ...lazyParams.filters.code,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            {/* tên  */}
            <div className="col-12 md:col-4">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "66px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="name"
                    >
                        {t("template.name")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="name"
                            name="name"
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        name: {
                                            ...lazyParams.filters.name,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            {/* Loại */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "80px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="type"
                    >
                        {t("template.type")}
                    </label>
                    <Dropdown
                        className="col"
                        value={lazyParams?.filters?.type?.value}
                        style={{
                            width: "100%",
                            margin: "5px 0px 0px 0px",
                        }}
                        showClear={true}
                        id="type"
                        name="type"
                        options={templateTypes}
                        onChange={(e) => {
                            setCheckLoad(false);
                            setLazyParams({
                                ...lazyParams,
                                filters: {
                                    ...lazyParams.filters,
                                    type: {
                                        ...lazyParams.filters.type,
                                        value: e.value,
                                    },
                                },
                            });
                        }}
                    ></Dropdown>
                </div>
            </div>

            <div className="col-12 md:col-1 text-right">
                <div className="grid custom-form">
                    <div className="col-12">
                        <label
                            style={{
                                // width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                        >
                            {" "}
                        </label>
                    </div>
                    <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                        <Button
                            label={t("search", { ns: "common" })}
                            icon="pi pi-search"
                            onClick={(e) => {
                                setCheckLoad(true);
                                setLazyParams({
                                    ...lazyParams,
                                    first: 0,
                                    page: 0,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("template.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("template.list")}>
                                <Toast ref={toast} />

                                <DataTable
                                    ref={dt}
                                    value={templates}
                                    selectionMode="single"
                                    selection={selectedTemplate}
                                    onSelectionChange={(e) => {
                                        setSelectedTemplate(e.value);
                                    }}
                                    dataKey="id"
                                    className="datatable-responsive p-datatable-sm"
                                    // globalFilter={globalFilter}
                                    // header={headerTemplate}
                                    showGridlines
                                    lazy
                                    paginator
                                    rows={lazyParams.rows}
                                    // rowsPerPageOptions={[10, 20, 50]}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="code" header={t("code")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="name" header={t("name")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="type" header={t("type")} sortable filter showFilterMatchModes={false} filterElement={templateTypeFilterTemplate} body={templateTypeBodyTemplate} style={{ minWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column
                                        field="enabled"
                                        header={t("enabled")}
                                        dataType="boolean"
                                        sortable
                                        filter
                                        filterElement={checkboxFilterTemplate}
                                        body={checkboxBodyTemplate}
                                        headerStyle={{ width: "10rem" }}
                                        style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}
                                    />
                                    <Column body={actionBodyTemplate} style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">{actionTemplate()}</div>
                    </div>

                    <Confirmation
                        visible={deleteTemplateDialog}
                        hideAction={hideDeleteTemplateDialog}
                        processAction={deleteTemplateAction}
                        confirmMessage={
                            template && (
                                <span>
                                    {t("confirm.delete", { ns: "common", object: t("template") })} <b>{template.name}</b>?
                                </span>
                            )
                        }
                    />

                    <Confirmation
                        visible={deleteTemplatesDialog}
                        hideAction={hideDeleteTemplatesDialog}
                        processAction={deleteSelectedTemplatesAction}
                        confirmMessage={
                            selectedTemplate && (
                                <span>
                                    {t("confirm.delete.selected", { ns: "common", object: t("templates") })} <b>{selectedTemplate.name}</b>?{" "}
                                </span>
                            )
                        }
                    />

                    <TemplateEditor />
                </div>
            </div>
        </div>
    );
};
